import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, Settings2 } from "lucide-react";
import { useState } from "react";
import { TenantFormData } from "@/lib/schemas/tenant";

interface Step3PlanLimitsProps {
  form: UseFormReturn<TenantFormData>;
}

const plans = [
  { id: "starter", name: "Starter", description: "For small businesses" },
  { id: "pro", name: "Pro", description: "For growing teams" },
  { id: "enterprise", name: "Enterprise", description: "For large organizations" },
];

export function Step3PlanLimits({ form }: Step3PlanLimitsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Plan & Limits</h2>
        <p className="text-sm text-muted-foreground">
          Select a plan and configure usage limits
        </p>
      </div>

      <FormField
        control={form.control}
        name="plan_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Plan *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{plan.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {plan.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <Collapsible open={showAdvanced} onOpenChange={setShowAdvanced}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-0 h-auto hover:bg-transparent">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Settings2 className="h-4 w-4" />
              Advanced Limit Overrides
            </div>
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground transition-transform ${
                showAdvanced ? "rotate-180" : ""
              }`}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <FormField
            control={form.control}
            name="max_users"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Users</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Leave empty for plan default"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value ? parseInt(value, 10) : null);
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Override the default user limit for this plan
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_phone_numbers"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max Phone Numbers</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Leave empty for plan default"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value ? parseInt(value, 10) : null);
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Override the default phone number limit
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="max_sms_per_day"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Max SMS per Day</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    placeholder="Leave empty for plan default"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value ? parseInt(value, 10) : null);
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Override the default daily SMS limit
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
